import React, { useEffect, useState } from 'react';
import { TextField, FormLabel } from '@mui/material';
import { useObjectContext } from '@/hooks/useObjectContext';
import { DealCardStageChange } from '../cards/DealCardValues/DealCardStageChange';
import { DealCardDateSelect } from '../cards/DealCardValues/DealCardDateSelect';
import { ClaimNumberField } from '../ClaimNumberField';
import { ClaimTypeSelectWrapper } from '../selects/ClaimTypeSelect';
import { useUpdateInsightClaimMutation } from '@/features/claims/ticketsApi';
import { formatDateDayToYear } from '@/utils/constants';
import { NumericFormat } from 'react-number-format';
import { useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import { insightMaxLengths } from './data';

const isCapitalised = (header) => /^[A-Z]/.test(header);

export const ClaimDetailField = ({ header, title, readOnly }) => {
	const { dealRow, claim } = useObjectContext();
	const isInsightField = isCapitalised(header);

	const [updateInsightClaim, { isLoading }] = useUpdateInsightClaimMutation();
	const [updateDealRow] = useUpdateDealRowMutation();

	const savedValue = isInsightField ? claim?.[header] : dealRow?.[header];
	const [value, setValue] = useState(savedValue ?? '');

	useEffect(() => {
		setValue(savedValue ?? '');
	}, [savedValue]);

	const handleBlur = async () => {
		if (readOnly || value === (savedValue ?? '')) return;

		try {
			if (isInsightField) {
				await updateInsightClaim({
					dealId: dealRow?.id,
					properties: {
						[header]: value,
					},
				}).unwrap();
			} else {
				await updateDealRow({
					dealId: dealRow?.id,
					docId: dealRow?.id,
					properties: {
						[header]: value,
					},
				}).unwrap();
			}
		} catch (error) {
			console.error(`Error updating claim field ${header}`, error);
		}
	};

	const getFormattedString = (claim) => {
		if (
			Object.keys(claim ?? {}).length > 0 &&
			claim?.PolicyTransactionFromDate &&
			claim?.PolicyTransactionToDate
		) {
			return `${formatDateDayToYear(
				claim?.PolicyTransactionFromDate,
			)} to ${formatDateDayToYear(
				claim?.PolicyTransactionToDate,
			)} (Effective ${formatDateDayToYear(claim?.PolicyTransactionEffectiveDate)})`;
		}
		return '';
	};

	const drawComponent = () => {
		const textFieldProps = {
			name: header,
			fullWidth: true,
			slotProps: {
				input: {
					...(readOnly && { readOnly: true }),
				},
			},
			value,
			onChange: (event) => setValue(event.target.value),
			onBlur: handleBlur,
			disabled: isLoading,
		};

		switch (header) {
			case 'dealStage':
				return <DealCardStageChange minimal={false} showLabel={false} />;
			case 'ReportedDate':
			case 'LossDate':
				return (
					<DealCardDateSelect
						dealKey={header}
						minimal={false}
						disableFuture={false}
						disablePast={false}
					/>
				);
			case 'claimNumber':
				return <ClaimNumberField minimal={false} />;
			case 'Excess':
			case 'EstimateOfLossAmount':
			case 'PaidAmount':
				return (
					<NumericFormat
						fullWidth
						customInput={TextField}
						thousandSeparator
						decimalScale={2}
						fixedDecimalScale
						allowNegative={false}
						prefix='$'
						value={value}
						onValueChange={({ floatValue }) => setValue(floatValue ?? null)}
						onBlur={handleBlur}
						style={{
							...(readOnly && { readOnly: true }),
						}}
					/>
				);
			case 'Status':
				return (
					<ClaimTypeSelectWrapper
						key={'ticket-card-value-claim-type'}
						noLabel={true}
						minimal={false}
					/>
				);
			case 'SubjectOfLoss': // This is a custom field that requires a larger text area for input, so we handle it separately.
				return (
					<TextField
						{...textFieldProps}
						placeholder='Enter details about the subject of loss...'
					/>
				);
			case 'PolicyPeriod':
				return (
					<TextField
						fullWidth
						slotProps={{
							input: {
								...(readOnly && { readOnly: true }),
							},
						}}
						value={getFormattedString(claim)}
						disabled={true}
					/>
				);
			case 'repairer':
			case 'assessor':
				return <TextField {...textFieldProps} />;
			case 'Description':
				return (
					<TextField
						{...textFieldProps}
						multiline
						rows={5}
						helperText={`${value.length}/${insightMaxLengths.Description}`}
						slotProps={{
							...textFieldProps.slotProps,
							htmlInput: { maxLength: insightMaxLengths.Description },
							formHelperText: {
								sx: {
									textAlign: 'right',
									...(value.length == insightMaxLengths.Description && {
										fontWeight: 'bold',
									}),
								},
							},
						}}
					/>
				);

			default:
				return <TextField {...textFieldProps} disabled={true} />;
		}
	};

	return (
		<>
			<FormLabel>{title}</FormLabel>
			{drawComponent()}
		</>
	);
};
